import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  Youtube,
  Zap,
  Target,
  AlignLeft,
  Settings,
  Sparkles,
  CheckCircle,
  FileText,
  Clock,
  PlayCircle,
  MessageCircle,
  TrendingUp,
  Search,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../firebase.init";
import { deductUserCredit } from "../../../../utils/geminiUtils";

const FullYoutubeScriptGenerator = ({ toolPath = "/user/script-generate" }) => {
  // --- AUTH & CORE STATE (UNCHANGED) ---
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // --- FORM STATE ---
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");
  const [videoLength, setVideoLength] = useState("8 minutes");
  const [tone, setTone] = useState("Casual");
  const [goal, setGoal] = useState("Growth");
  const [includeCta, setIncludeCta] = useState(true);
  const [includeHooks, setIncludeHooks] = useState(true);
  const [includeSeo, setIncludeSeo] = useState(true);

  // --- OUTPUT STATE ---
  const [scriptData, setScriptData] = useState(null);

  // --- CREDIT & TOOL CONFIG (UNCHANGED) ---

  const [creditCost, setCreditCost] = useState(2);
  const [apiKeys, setApiKeys] = useState([]);

  // --- OPTIONS ---
  const lengthOptions = ["5 minutes", "8 minutes", "10 minutes", "15+ minutes"];
  const toneOptions = [
    "Professional",
    "Casual",
    "Energetic",
    "Educational",
    "Storytelling",
    "Persuasive",
  ];
  const goalOptions = ["Growth", "Engagement", "Sales", "Authority"];
  const audienceOptions = [
    "General Audience",
    "Beginners",
    "Experts",
    "Professionals",
    "Gamers",
    "Students",
  ];

  // Fetch API keys (Unchanged)
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0)
          setError("No API keys available. Please contact administrator.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch Tools List (Unchanged)
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/tools-section`,
      );
      if (
        response.data &&
        response.data.success &&
        response.data.data &&
        response.data.data.tools
      ) {
        const tool = response.data.data.tools.find((t) => t.id === 4);
        if (tool) {
          setCreditCost(tool.creditCost);
        }
      }
    } catch (err) {
      console.warn("Could not fetch tools list, using default credit cost");
    } finally {
      setLoadingTools(false);
    }
  };

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Initial Data Fetch (Unchanged)
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        setLoadingTools(false);
        return;
      }
      //       try {
      //         await Promise.all([fetchApiKeys(), fetchToolsList()]);

      //         const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
      //           params: { userEmail: user.email },
      //         });
      //         if (userResponse.data && userResponse.data.length > 0) {
      //           const userData = userResponse.data[0];
      //
      //         } else {
      //           setError("User data not found");
      //         }
      //       } catch (err) {
      //         console.error("Error fetching data:", err);
      //         setError("Failed to load data");
      //       } finally {
      //         setLoading(false);
      //       }
      //     };
      //     fetchData();
      //   }, [user, loadingAuth]);

      //   // Prompt Engineering for FULL Video Script
      //   const constructPrompt = () => {
      //     return `You are a world-class YouTube scriptwriter and audience retention expert. Write a FULL, highly engaging, teleprompter-ready video script based on these parameters.

      //     PARAMETERS:
      //     - Topic: ${topic}
      //     - Target Audience: ${audience}
      //     - Target Video Length: ${videoLength}
      //     - Tone: ${tone}
      //     - Primary Goal: ${goal}

      //     WRITING RULES:
      //     1. Write the ACTUAL spoken script. Do not just write an outline. Write the full comprehensive text that the creator will read out loud.
      //     2. Write in a natural, conversational, human-like style. Keep sentences short and punchy. Avoid robotic corporate language.
      //     3. Use storytelling, metaphors, and pattern interrupts to maximize viewer retention.
      //     4. Provide clear transitions between sections.

      //     CRITICAL INSTRUCTION:
      //     Respond ONLY with a raw, valid JSON object. Do not include markdown blocks like \`\`\`json.

      //     JSON SCHEMA REQUIRED:
      //     {
      //       "title": "A highly clickable, CTR-optimized suggested title for the video",
      //       "hooks": [${includeHooks ? '"Hook Variation 1 (curiosity gap)", "Hook Variation 2 (problem/solution)", "Hook Variation 3 (bold claim)"' : '"One incredibly strong, attention-grabbing hook"'}],
      //       "intro": "The setup. Introduce the topic, explain exactly what the viewer will learn, and create anticipation (15-30 seconds of speaking).",
      //       "mainContent": [
      //         {
      //           "heading": "Clear Section Heading (e.g., [02:00] The Secret Method)",
      //           "script": "The FULL, detailed spoken script for this section. Write multiple paragraphs of high-value, teleprompter-ready text."
      //         }
      //         // Generate enough detailed sections to match the requested video length of ${videoLength}.
      //       ],
      //       "engagement": "A natural, non-pushy question or prompt to ask the audience to comment or like.",
      //       "cta": "${includeCta ? "A strong, specific Call to Action tailored to the goal (" + goal + ")." : ""}",
      //       "outro": "A quick recap, reinforcement of value, and a strong closing statement.",
      //       "seoDescription": "${includeSeo ? "A highly optimized YouTube description snippet containing primary keywords." : ""}"
      //     }`;
      //   };

      //   // Generate content (Credit deduction logic exactly as requested)
      //   const generateFullScript = async () => {
      //     if (!topic.trim() || !audience.trim()) {
      //       setError("Please provide both a Video Topic and Target Audience.");
      //       return;
      //     }
      //     if (apiKeys.length === 0) {
      //       setError("No API keys available. Please try again later.");
      //       return;
      //     }
      //
      try {
        await Promise.all([fetchApiKeys(), fetchToolsList()]);

        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Prompt Engineering for FULL Video Script
  const constructPrompt = () => {
    return `You are a world-class YouTube scriptwriter and audience retention expert. Write a FULL, highly engaging, teleprompter-ready video script based on these parameters. 

    PARAMETERS:
    - Topic: ${topic}
    - Target Audience: ${audience}
    - Target Video Length: ${videoLength}
    - Tone: ${tone}
    - Primary Goal: ${goal}
    
    WRITING RULES:
    1. Write the ACTUAL spoken script. Do not just write an outline. Write the full comprehensive text that the creator will read out loud.
    2. Write in a natural, conversational, human-like style. Keep sentences short and punchy. Avoid robotic corporate language.
    3. Use storytelling, metaphors, and pattern interrupts to maximize viewer retention.
    4. Provide clear transitions between sections.
    
    CRITICAL INSTRUCTION:
    Respond ONLY with a raw, valid JSON object. Do not include markdown blocks like \`\`\`json.
    
    JSON SCHEMA REQUIRED:
    {
      "title": "A highly clickable, CTR-optimized suggested title for the video",
      "hooks": [${includeHooks ? '"Hook Variation 1 (curiosity gap)", "Hook Variation 2 (problem/solution)", "Hook Variation 3 (bold claim)"' : '"One incredibly strong, attention-grabbing hook"'}],
      "intro": "The setup. Introduce the topic, explain exactly what the viewer will learn, and create anticipation (15-30 seconds of speaking).",
      "mainContent": [
        {
          "heading": "Clear Section Heading (e.g., [02:00] The Secret Method)",
          "script": "The FULL, detailed spoken script for this section. Write multiple paragraphs of high-value, teleprompter-ready text."
        }
        // Generate enough detailed sections to match the requested video length of ${videoLength}.
      ],
      "engagement": "A natural, non-pushy question or prompt to ask the audience to comment or like.",
      "cta": "${includeCta ? "A strong, specific Call to Action tailored to the goal (" + goal + ")." : ""}",
      "outro": "A quick recap, reinforcement of value, and a strong closing statement.",
      "seoDescription": "${includeSeo ? "A highly optimized YouTube description snippet containing primary keywords." : ""}"
    }`;
  };

  // Generate content (Credit deduction logic exactly as requested)
  const generateFullScript = async () => {
    if (!topic.trim() || !audience.trim()) {
      setError("Please provide both a Video Topic and Target Audience.");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }

    setGenerating(true);
    setError("");
    setScriptData(null);

    try {
      // Deduct credits (Unchanged)

      // Call AI
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.8,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 5000, // Increased for full script generation
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        let generatedText = data.candidates[0].content.parts[0].text;

        // Clean markdown backticks to parse JSON safely
        generatedText = generatedText
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();

        try {
          const parsedContent = JSON.parse(generatedText);
          setScriptData(parsedContent);
          toast.success("Full YouTube script generated successfully!");
        } catch (parseErr) {
          throw new Error("AI returned invalid data format. Please try again.");
        }
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      console.error("Error generating content:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const getFormattedScript = () => {
    if (!scriptData) return "";
    let text = `TITLE: ${scriptData.title}\n\n`;

    text += `=== THE HOOK(S) ===\n`;
    scriptData.hooks.forEach(
      (hook, i) => (text += `Option ${i + 1}: ${hook}\n\n`),
    );

    text += `=== INTRO ===\n${scriptData.intro}\n\n`;

    text += `=== MAIN CONTENT ===\n`;
    scriptData.mainContent.forEach((sec) => {
      text += `[${sec.heading}]\n${sec.script}\n\n`;
    });

    if (scriptData.engagement) {
      text += `=== ENGAGEMENT ===\n${scriptData.engagement}\n\n`;
    }

    if (scriptData.cta) {
      text += `=== CALL TO ACTION ===\n${scriptData.cta}\n\n`;
    }

    text += `=== OUTRO ===\n${scriptData.outro}\n\n`;

    if (scriptData.seoDescription) {
      text += `=== SEO DESCRIPTION ===\n${scriptData.seoDescription}\n`;
    }

    return text;
  };

  const copyToClipboard = () => {
    const text = getFormattedScript();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`Full script copied to clipboard!`);
      })
      .catch(() => {
        toast.error(`Failed to copy script`);
      });
  };

  const downloadContent = () => {
    const text = getFormattedScript();
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${topic.replace(/\s+/g, "-")}-full-script.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Script downloaded!");
  };

  if (loadingAuth || loading || loadingKeys || loadingTools) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              border: "4px solid #f1f5f9",
              borderTop: "4px solid #FF0000",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              margin: "0 auto 10px",
            }}
          ></div>
          <p style={{ color: "#64748b", fontWeight: "500" }}>
            Loading Workspace...
          </p>
        </div>

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "3rem",
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <Youtube size={48} color="#FF0000" style={{ marginBottom: "1rem" }} />
          <h2
            style={{
              color: "#0f172a",
              marginBottom: "1.5rem",
              fontSize: "1.5rem",
              fontWeight: "700",
            }}
          >
            Authentication Required
          </h2>
          <button
            onClick={() => (window.location.href = "/login")}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #FF0000 0%, #cc0000 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              width: "100%",
            }}
          >
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .yt-script-app { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; color: #334155; }
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; padding: 1.75rem; transition: all 0.3s ease; height: 100%; }
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control, .form-select { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 1.25rem; box-sizing: border-box; }
        .form-control:focus, .form-select:focus { outline: none; border-color: #FF0000; box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1); background: #fff; }
        textarea.form-control { resize: vertical; min-height: 80px; }
        
        .gradient-btn { background: linear-gradient(135deg, #FF0000 0%, #D22B2B 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(255, 0, 0, 0.2); }
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(255, 0, 0, 0.3); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
        
        .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; border-radius: 34px; }
        .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
        input:checked + .slider { background-color: #10b981; }
        input:checked + .slider:before { transform: translateX(20px); }
        
        .script-block { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 1.5rem; margin-bottom: 1.5rem; }
        .script-badge { background: #e2e8f0; color: #475569; padding: 0.4em 0.8em; border-radius: 6px; font-weight: 700; font-size: 0.75rem; display: inline-flex; align-items: center; gap: 6px; margin-bottom: 1rem; text-transform: uppercase; letter-spacing: 0.05em; }
        .script-text { font-family: 'Inter', system-ui, sans-serif; font-size: 1.05rem; line-height: 1.8; color: #1e293b; margin: 0; white-space: pre-wrap; }
        .script-heading { font-size: 1.15rem; font-weight: 800; color: #0f172a; margin-bottom: 0.75rem; }
        
        @keyframes pulse-custom { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .animate-pulse-custom { animation: pulse-custom 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>

      <div className="yt-script-app">
        <div
          className="container"
          style={{ maxWidth: "1400px", margin: "0 auto" }}
        >
          {/* Top Navigation */}
          <div
            className="d-flex justify-content-between align-items-center mb-4"
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "2rem",
            }}
          >
            <button
              onClick={() => window.history.back()}
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                padding: "8px 16px",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "600",
                color: "#64748b",
              }}
            >
              ← Dashboard
            </button>
          </div>

          {/* Header */}
          <div
            className="text-center mb-5"
            style={{ textAlign: "center", marginBottom: "3rem" }}
          >
            <h1
              style={{
                fontSize: "2.5rem",
                fontWeight: "800",
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                letterSpacing: "-0.02em",
              }}
            >
              <Youtube color="#FF0000" size={40} /> Full Video Script Generator
            </h1>
            <p
              style={{
                color: "#64748b",
                marginTop: "12px",
                fontSize: "1.1rem",
                maxWidth: "650px",
                margin: "12px auto 0",
              }}
            >
              Generate complete, engaging, and retention-optimized YouTube
              scripts ready for the teleprompter.
            </p>
          </div>

          {error && (
            <div
              style={{
                background: "#fef2f2",
                color: "#ef4444",
                padding: "1rem",
                borderRadius: "10px",
                marginBottom: "2rem",
                textAlign: "center",
                border: "1px solid #fecaca",
                fontWeight: "600",
              }}
            >
              {error}
            </div>
          )}

          <div
            className="row"
            style={{ display: "flex", flexWrap: "wrap", gap: "2rem" }}
          >
            {/* Left Column - Input Form */}
            <div
              className="col-lg-4 col-md-5"
              style={{ flex: "1 1 350px", maxWidth: "450px" }}
            >
              <div className="saas-card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "1.5rem",
                    paddingBottom: "1rem",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <Target size={20} color="#FF0000" />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.25rem",
                      color: "#0f172a",
                      fontWeight: "700",
                    }}
                  >
                    Script Parameters
                  </h3>
                </div>

                <label className="form-label">
                  Video Topic <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., How to start an online business with $0, iPhone 16 Pro Max Full Review..."
                />

                <label className="form-label">
                  Target Audience <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  className="form-select"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  {audienceOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <div style={{ display: "flex", gap: "1rem" }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Video Length</label>
                    <select
                      className="form-select"
                      value={videoLength}
                      onChange={(e) => setVideoLength(e.target.value)}
                    >
                      {lengthOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Tone</label>
                    <select
                      className="form-select"
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                    >
                      {toneOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <label className="form-label">Primary Goal</label>
                <select
                  className="form-select"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                >
                  {goalOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "1.25rem",
                    borderRadius: "12px",
                    marginTop: "0.5rem",
                    marginBottom: "1.5rem",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#1e293b",
                          display: "block",
                          fontSize: "0.95rem",
                        }}
                      >
                        Multiple Hooks
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        Generate 3 variations
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={includeHooks}
                        onChange={(e) => setIncludeHooks(e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#1e293b",
                          display: "block",
                          fontSize: "0.95rem",
                        }}
                      >
                        Strong CTA
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        Include subscribe/sales push
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={includeCta}
                        onChange={(e) => setIncludeCta(e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#1e293b",
                          display: "block",
                          fontSize: "0.95rem",
                        }}
                      >
                        SEO Description
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        Generate YouTube snippet
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={includeSeo}
                        onChange={(e) => setIncludeSeo(e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                <button
                  onClick={generateFullScript}
                  disabled={generating || !topic.trim() || !audience.trim()}
                  className="gradient-btn"
                >
                  {generating ? (
                    <Clock size={18} className="animate-spin" />
                  ) : (
                    <Sparkles size={18} />
                  )}
                  {generating ? "Writing Script..." : `Generate Full Script `}
                </button>
              </div>
            </div>

            {/* Right Column - Output */}
            <div className="col-lg-8 col-md-7" style={{ flex: "1 1 500px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    color: "#0f172a",
                    fontWeight: "700",
                  }}
                >
                  Generated Video Script
                </h2>
                {scriptData && (
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={copyToClipboard}
                      style={{
                        background: "#fff",
                        color: "#334155",
                        border: "1px solid #cbd5e1",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                      }}
                    >
                      <Copy size={16} /> Copy
                    </button>
                    <button
                      onClick={downloadContent}
                      style={{
                        background: "#10b981",
                        color: "#fff",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                      }}
                    >
                      <Download size={16} /> Download
                    </button>
                  </div>
                )}
              </div>

              {generating ? (
                <div
                  className="saas-card animate-pulse-custom"
                  style={{
                    textAlign: "center",
                    padding: "6rem 2rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      background: "#fee2e2",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1.5rem",
                    }}
                  >
                    <FileText size={34} color="#FF0000" />
                  </div>
                  <h3
                    style={{
                      color: "#0f172a",
                      fontWeight: "800",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Writing Your Masterpiece...
                  </h3>
                  <p
                    style={{
                      color: "#64748b",
                      maxWidth: "350px",
                      margin: "0 auto",
                    }}
                  >
                    Crafting hooks, injecting retention triggers, and writing
                    the full teleprompter script.
                  </p>
                </div>
              ) : scriptData ? (
                <div className="saas-card">
                  <h2
                    style={{
                      fontSize: "1.6rem",
                      fontWeight: "800",
                      color: "#0f172a",
                      marginBottom: "2rem",
                      borderBottom: "2px solid #f1f5f9",
                      paddingBottom: "1rem",
                      lineHeight: "1.4",
                    }}
                  >
                    {scriptData.title}
                  </h2>

                  {/* Hooks Block */}
                  {scriptData.hooks && scriptData.hooks.length > 0 && (
                    <div
                      className="script-block"
                      style={{ borderLeft: "4px solid #ef4444" }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#fef2f2", color: "#b91c1c" }}
                      >
                        <Zap size={14} fill="currentColor" /> The Hook (0:00 -
                        0:15)
                      </div>
                      {scriptData.hooks.map((hook, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom:
                              idx !== scriptData.hooks.length - 1
                                ? "1.25rem"
                                : 0,
                          }}
                        >
                          {scriptData.hooks.length > 1 && (
                            <strong
                              style={{
                                color: "#ef4444",
                                fontSize: "0.85rem",
                                display: "block",
                                marginBottom: "6px",
                              }}
                            >
                              OPTION {idx + 1}:
                            </strong>
                          )}
                          <p className="script-text">"{hook}"</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Intro Block */}
                  {scriptData.intro && (
                    <div
                      className="script-block"
                      style={{ borderLeft: "4px solid #3b82f6" }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#eff6ff", color: "#1d4ed8" }}
                      >
                        <PlayCircle size={14} /> Intro & Setup
                      </div>
                      <p className="script-text">{scriptData.intro}</p>
                    </div>
                  )}

                  {/* Main Content Sections */}
                  {scriptData.mainContent &&
                    scriptData.mainContent.length > 0 && (
                      <div
                        className="script-block"
                        style={{
                          borderLeft: "4px solid #8b5cf6",
                          background: "#fff",
                        }}
                      >
                        <div
                          className="script-badge"
                          style={{ background: "#f5f3ff", color: "#6d28d9" }}
                        >
                          <AlignLeft size={14} /> Main Content Script
                        </div>
                        {scriptData.mainContent.map((section, idx) => (
                          <div
                            key={idx}
                            style={{
                              marginBottom:
                                idx !== scriptData.mainContent.length - 1
                                  ? "2.5rem"
                                  : 0,
                            }}
                          >
                            <h4 className="script-heading">
                              {section.heading}
                            </h4>
                            <p className="script-text">{section.script}</p>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Engagement Prompt */}
                  {scriptData.engagement && (
                    <div
                      className="script-block"
                      style={{
                        borderLeft: "4px solid #f59e0b",
                        background: "#fffbeb",
                      }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#fef3c7", color: "#b45309" }}
                      >
                        <MessageCircle size={14} /> Engagement Trigger
                      </div>
                      <p
                        className="script-text"
                        style={{ fontStyle: "italic", fontWeight: "500" }}
                      >
                        {scriptData.engagement}
                      </p>
                    </div>
                  )}

                  {/* Call To Action */}
                  {scriptData.cta && (
                    <div
                      className="script-block"
                      style={{ borderLeft: "4px solid #10b981" }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#ecfdf5", color: "#047857" }}
                      >
                        <Target size={14} /> Call To Action
                      </div>
                      <p className="script-text">{scriptData.cta}</p>
                    </div>
                  )}

                  {/* Outro */}
                  {scriptData.outro && (
                    <div
                      className="script-block"
                      style={{ borderLeft: "4px solid #64748b" }}
                    >
                      <div className="script-badge">
                        <CheckCircle size={14} /> Outro & Sign-off
                      </div>
                      <p className="script-text">{scriptData.outro}</p>
                    </div>
                  )}

                  {/* SEO Description */}
                  {scriptData.seoDescription && (
                    <div
                      className="script-block"
                      style={{
                        borderLeft: "4px solid #0ea5e9",
                        marginBottom: 0,
                      }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#e0f2fe", color: "#0369a1" }}
                      >
                        <Search size={14} /> SEO Description Snippet
                      </div>
                      <p
                        className="script-text"
                        style={{ fontSize: "0.95rem" }}
                      >
                        {scriptData.seoDescription}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className="saas-card"
                  style={{
                    textAlign: "center",
                    padding: "6rem 2rem",
                    border: "2px dashed #cbd5e1",
                    background: "transparent",
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileText
                    size={56}
                    color="#cbd5e1"
                    style={{ marginBottom: "1.5rem" }}
                  />
                  <h3
                    style={{
                      color: "#64748b",
                      fontWeight: "700",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    Ready to Write
                  </h3>
                  <p style={{ color: "#94a3b8", margin: 0, maxWidth: "350px" }}>
                    Fill out the required parameters on the left to generate a
                    complete YouTube video script.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FullYoutubeScriptGenerator;
